import type { components } from "@/api-schema";
import { CustomerType, DmsSystem } from "@/api-schema-enums";
import { CUSTOMER_TYPE_COLORS, getCustomerTypeLabel } from "@/common/helpers/customer-display";
import { DeviceType } from "@/common/hooks/use-device";
import { ContactLink } from "@/components/contact-link/contact-link";
import { CopyableValue } from "@/components/copyable-value/copyable-value";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { Badge, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";
import { MailIcon, PhoneIcon, SmartphoneIcon } from "lucide-react";
import { Fragment } from "react";
import { AddressLines } from "../_components/address-lines";
import { commonColumns } from "../_constants/common-columns";
import { getBillingAddress } from "./_helpers/get-billing-address";
import { getCustomerDisplayName } from "./_helpers/get-customer-display-name";
import { getIdentifierRows } from "./_helpers/get-identifier-rows";

type CustomerTableType = components["schemas"]["Customer"] & RowWithIdentifier;
type Address = components["schemas"]["Address"];
type CustomerIdentifier = NonNullable<CustomerTableType["customerIdentifiers"]>[number];

interface ContactInfo {
  email: string | null | undefined;
  mobile: string | null | undefined;
  landline: string | null | undefined;
}

interface NameAndContactInfo extends ContactInfo {
  name: string;
}

interface IdentifierInfo {
  identifiers: CustomerIdentifier[] | undefined;
}

export const customerTableDefaultColumns = {
  columnName: {
    id: "lastName",
    exportPaths: ["customerCompanyName", "lastName", "firstName"],
    size: 220,
    enableSorting: true,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("common:Name"),
    accessorFn: (row: CustomerTableType) => getCustomerDisplayName(row),
    cell: (info) => automaticCellRenderer(info.getValue()),
  } as ImplementedAccessorColumnDef<CustomerTableType, string>,

  columnNameAndContact: {
    id: "nameAndContact",
    exportPaths: [],
    size: 320,
    enableSorting: false,
    visibility: [DeviceType.Mobile, DeviceType.Tablet],
    header: () => i18next.t("common:NameAndContact"),
    accessorFn: (row: CustomerTableType) => ({
      name: getCustomerDisplayName(row),
      email: row.email,
      mobile: row.mobile,
      landline: row.landline,
    }),
    cell: (info) => {
      const { name, email, mobile, landline } = info.getValue();

      return (
        <div className="flex flex-col gap-2 text-left">
          <div>{name || <EmptyValue />}</div>
          {(email || mobile || landline) && (
            <div className="flex flex-col gap-1 whitespace-nowrap">
              {email && <ContactLink href={`mailto:${email}`} icon={MailIcon} label={email} />}
              {mobile && <ContactLink href={`tel:${mobile}`} icon={SmartphoneIcon} label={mobile} />}
              {landline && <ContactLink href={`tel:${landline}`} icon={PhoneIcon} label={landline} />}
            </div>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<CustomerTableType, NameAndContactInfo>,

  columnAddress: {
    id: "address",
    exportPaths: [
      "addresses[type=Billing].address.street",
      "addresses[type=Billing].address.houseNumber",
      "addresses[type=Billing].address.postalCode",
      "addresses[type=Billing].address.city",
    ],
    size: 240,
    enableSorting: false,
    header: () => i18next.t("common:Address"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CustomerTableType) => getBillingAddress(row),
    cell: (info) => <AddressLines address={info.getValue()} fallback={<EmptyValue />} />,
  } as ImplementedAccessorColumnDef<CustomerTableType, Address | null>,

  columnContact: {
    id: "contact",
    exportPaths: ["email", "mobile", "landline"],
    size: 240,
    enableSorting: false,
    header: () => i18next.t("common:Contact"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CustomerTableType) => ({
      email: row.email,
      mobile: row.mobile,
      landline: row.landline,
    }),
    cell: (info) => {
      const contact = info.getValue();

      if (!contact.email && !contact.mobile && !contact.landline) {
        return <EmptyValue />;
      }

      return (
        <div className="flex flex-col gap-1 whitespace-nowrap">
          {contact.email && <ContactLink href={`mailto:${contact.email}`} icon={MailIcon} label={contact.email} />}
          {contact.mobile && (
            <ContactLink href={`tel:${contact.mobile}`} icon={SmartphoneIcon} label={contact.mobile} />
          )}
          {contact.landline && (
            <ContactLink href={`tel:${contact.landline}`} icon={PhoneIcon} label={contact.landline} />
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<CustomerTableType, ContactInfo>,

  columnIds: {
    id: "ids",
    exportPaths: Object.values(DmsSystem).map((type) => `customerIdentifiers[type=${type}].identifier`),
    size: 300,
    enableSorting: false,
    header: () => i18next.t("masterdata:Identifiers"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CustomerTableType) => ({
      identifiers: row.customerIdentifiers,
    }),
    cell: (info) => {
      const rows = getIdentifierRows(info.getValue());

      if (rows.length === 0) {
        return <EmptyValue />;
      }

      return (
        <div className="grid grid-cols-[10rem_1fr] items-baseline gap-x-2 gap-y-1 text-left text-sm">
          {rows.map((row) => (
            <Fragment key={row.key}>
              <span className="text-muted-foreground min-w-0 text-xs wrap-break-word whitespace-normal">
                {row.label}
              </span>
              <CopyableValue value={row.value} />
            </Fragment>
          ))}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<CustomerTableType, IdentifierInfo>,

  columnType: {
    id: "type",
    size: 110,
    enableSorting: true,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("masterdata:CustomerType"),
    accessorFn: (row: CustomerTableType) => row.type,
    cell: (info) => {
      const type = info.getValue();
      if (!type) return <EmptyValue />;
      return (
        <Badge variant={BadgeVariant.Outlined} colorVariant={CUSTOMER_TYPE_COLORS[type]}>
          {getCustomerTypeLabel(type)}
        </Badge>
      );
    },
  } as ImplementedAccessorColumnDef<CustomerTableType, CustomerType>,

  columnCreatedAt: commonColumns.columnCreatedAt<CustomerTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<CustomerTableType>(),
  columnDeletedAt: commonColumns.columnDeletedAt<CustomerTableType>(),
  columnUuid: commonColumns.columnUuid<CustomerTableType>(),
};
